import { FC } from 'react';
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  InputAdornment,
  Button,
} from '@mui/material';
import { IconSearch, IconFilter, IconX } from '@tabler/icons-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import {
  BOOKING_STATUSES,
  BOOKING_STATUS_MAP,
} from '../../../../constants/bookingStatus';
import useGetPartnerBoats from '../../../../hooks/useGetPartnerBoats';

interface BookingFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  statusFilter: number | 'all';
  onStatusChange: (value: number | 'all') => void;
  boatFilter: number | 'all';
  onBoatChange: (value: number | 'all') => void;
  startDate: Dayjs | null;
  onStartDateChange: (value: Dayjs | null) => void;
  endDate: Dayjs | null;
  onEndDateChange: (value: Dayjs | null) => void;
  onClearAll?: () => void;
}

const BookingFilters: FC<BookingFiltersProps> = ({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusChange,
  boatFilter,
  onBoatChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  onClearAll,
}) => {
  const { boats } = useGetPartnerBoats();

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    statusFilter !== 'all' ||
    boatFilter !== 'all' ||
    startDate !== null ||
    endDate !== null;

  return (
    <Box className="mb-6 space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-700">
          <IconFilter className="h-5 w-5" />
          <h3 className="text-sm font-semibold">Filters</h3>
          {hasActiveFilters && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              Active
            </span>
          )}
        </div>
        {hasActiveFilters && onClearAll && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<IconX className="h-4 w-4" />}
            onClick={onClearAll}
            className="text-gray-600"
          >
            Clear All
          </Button>
        )}
      </div>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by booking ID, guest name..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconSearch className="h-5 w-5 text-gray-400" />
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Status Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={e => onStatusChange(e.target.value as number | 'all')}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {BOOKING_STATUSES.map(status => (
                <MenuItem key={status} value={status}>
                  {BOOKING_STATUS_MAP[status].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Boat Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Boat</InputLabel>
            <Select
              value={boatFilter}
              label="Boat"
              onChange={e => onBoatChange(e.target.value as number | 'all')}
            >
              <MenuItem value="all">All Boats</MenuItem>
              {boats.map(boat => (
                <MenuItem key={boat.boatId} value={boat.boatId}>
                  {boat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date Range - Start Date */}
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={newValue => onStartDateChange(newValue)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
              },
            }}
          />

          {/* Date Range - End Date */}
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={newValue => onEndDateChange(newValue)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
              },
            }}
          />
        </div>
      </LocalizationProvider>
    </Box>
  );
};

export default BookingFilters;
